import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@material-ui/core';
import { compact, keyBy } from 'lodash';
import { useMemo, useState } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../components/AdditionalActionsMenu';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { Form } from '../../../../../components/Form';
import { FormField } from '../../../../../components/Form/FormField';
import { FormRow } from '../../../../../components/Form/FormRow';
import { FormSubmitButton } from '../../../../../components/Form/FormSubmitButton';
import { required } from '../../../../../components/Form/validators';
import {
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import {
  TableSearch,
  TableToolbar,
  TableToolbarSection
} from '../../../../../components/Table';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { Section } from '../../../../../layout/Section';
import {
  auth,
  combineLoadingValues,
  setDoc,
  useMappedLoadingValue
} from '../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../services/firebaseFunctions';
import { formatDate, toMoment } from '../../../../../services/time';
import { toFullName, toSearchTermRegexp } from '../../../../../services/user';
import {
  getNewAdminUserdoc,
  useAllAdminUsers
} from '../../../../services/adminUsers';
import { useAllOrgs } from '../../../../services/orgs';

type NewAdminValues = {
  id: string;
  email: string;

  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;

  avatar: string;
};

const CreateAdminUserDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const initialValues: NewAdminValues = {
    id: '',
    firstName: '',
    middleName: '',
    lastName: '',
    preferredName: '',
    avatar: '',
    email: ''
  };

  const onSubmit = async (vs: NewAdminValues) => {
    let { id, ...others } = vs;
    if (!id) {
      const fbUser = await callFirebaseFunction<{ uid: string }>(
        'user-createNewFbUserWithEmailAndPassword',
        {
          email: others.email
        }
      );
      id = fbUser.uid;
    }
    const doc = getNewAdminUserdoc(id, others);
    await setDoc(doc);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <Form<NewAdminValues> initialValues={initialValues} onSubmit={onSubmit}>
        {({ submitting, invalid }) => (
          <>
            <DialogTitle>Create Admin User</DialogTitle>
            <DialogContent>
              <FormRow>
                <FormField<NewAdminValues['id']> name="id">
                  {({ input }) => (
                    <TextField
                      label="id"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                      helperText="Leave empty to create a new Firebase User, or enter an ID of an existing account"
                    />
                  )}
                </FormField>
              </FormRow>
              <FormRow>
                <FormField<NewAdminValues['email']>
                  name="email"
                  validate={required}
                >
                  {({ input }) => (
                    <TextField
                      label="Email"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
              <FormRow>
                <FormField<NewAdminValues['firstName']>
                  name="firstName"
                  validate={required}
                >
                  {({ input }) => (
                    <TextField
                      label="First Name"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
                <FormField<NewAdminValues['middleName']> name="middleName">
                  {({ input }) => (
                    <TextField
                      label="Middle Name"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
                <FormField<NewAdminValues['lastName']> name="lastName">
                  {({ input }) => (
                    <TextField
                      label="Last Name"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
              <FormRow>
                <FormField<
                  NewAdminValues['preferredName']
                > name="preferredName">
                  {({ input }) => (
                    <TextField
                      label="Preferred Name"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
              <FormRow>
                <FormField<NewAdminValues['avatar']> name="avatar">
                  {({ input }) => (
                    <TextField
                      label="Avatar URL"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <FormSubmitButton
                variant="contained"
                color="primary"
                submitting={submitting}
                submitComponent="Creating..."
                disabled={invalid}
              >
                Create
              </FormSubmitButton>
            </DialogActions>
          </>
        )}
      </Form>
    </Dialog>
  );
};

interface IAdminUserWithWOrgs {
  admin: Doc<IAdminUser>;
  orgs: Doc<IOrganization>[];
}

type ColumnName = 'id' | 'email' | 'name' | 'orgs' | 'createdAt' | 'actions';

type D = IAdminUserWithWOrgs;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'copyUserId',
        label: 'Copy User ID',
        onClick: () => {
          navigator.clipboard.writeText(d.admin.id);
        }
      },
      {
        key: 'sendPasswordMail',
        label: 'Send Password Reset mail',
        dialog: ({ onClose }) => {
          return (
            <>
              <DialogTitle>Send password reset email?</DialogTitle>
              <DialogContent>
                Confirm to send this mail to{' '}
                <strong>{d.admin.data.email}</strong>.
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <ButtonWithPromise
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    auth()
                      .sendPasswordResetEmail(d.admin.data.email)
                      .then(() =>
                        console.log(
                          `password reset email sent to ${d.admin.data.email}`
                        )
                      )
                      .catch((err) => {
                        console.log(
                          `failed to send reset password mail to ${d.admin.data.email}`,
                          err
                        );
                      })
                  }
                  pending="Sending..."
                >
                  Send
                </ButtonWithPromise>
              </DialogActions>
            </>
          );
        }
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'id',
    head: () => 'ID',
    cell: (d) => <small>{d.admin.id}</small>,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 240,
    flexGrow: 0
  },
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => d.admin.data.email,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => `${d.admin.data.lastName} ${d.admin.data.preferredName}`,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'orgs',
    head: () => 'Orgs',
    cell: (d) => d.orgs.map((d) => d.data.name).join(', '), // TODO create links
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 3
  },
  {
    key: 'createdAt',
    head: () => 'Created at',
    cell: (d) => formatDate(toMoment(d.admin.data.createdAt), 'YYYY/MM/DD'),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 90,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  id: {
    key: 'id',
    items: { sort: (d) => d.admin.id, dir: 'asc' }
  },
  email: {
    key: 'email',
    items: { sort: (d) => d.admin.data.email, dir: 'asc' }
  },
  name: {
    key: 'name',
    items: {
      sort: [(d) => d.admin.data.lastName, (d) => d.admin.data.preferredName],
      dir: 'asc'
    }
  },
  createdAt: {
    key: 'createdAt',
    items: { sort: (d) => d.admin.data.createdAt.toMillis(), dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.email;

const useAllAdminUsersWithOrganizations = () => {
  return useMappedLoadingValue(
    combineLoadingValues(useAllAdminUsers(), useAllOrgs()),
    ([adminUsers, orgs]) => {
      const orgsById = keyBy(orgs, (o) => o.id);
      return adminUsers.map((admin) => {
        return {
          admin,
          orgs: compact(
            admin.data.orgs.map((orgId) => {
              const org = orgsById[orgId];
              if (!org) {
                console.log('Unknown org!', admin, orgId);
                return null;
              }
              return org;
            })
          )
        };
      });
    }
  );
};
const ROW_TO_KEY = (d: D) => d.admin.id;

export const PageAdminUsersOverview = () => {
  const [ds, loading, error] = useAllAdminUsersWithOrganizations();
  console.log(ds, loading, error);
  const { dialogOpen, openDialog, closeDialog } = useDialogState();
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  const [q, setQ] = useState('');

  const filteredDs = useMemo(() => {
    if (!ds) {
      return null;
    }
    if (!q) {
      return ds;
    }
    return ds.filter((d) => {
      const term = toSearchTermRegexp(q);
      return !!(
        d.admin.data.email.match(term) || toFullName(d.admin.data).match(term)
      );
    });
  }, [ds, q]);

  return (
    <Section>
      <TableToolbar>
        <TableSearch term={q} onChange={setQ} autoFocus />
        <TableToolbarSection>
          <Button variant="contained" color="primary" onClick={openDialog}>
            Create admin user...
          </Button>
        </TableToolbarSection>
      </TableToolbar>
      {loading && <Loader height={500} />}
      {filteredDs && (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={filteredDs}
          rowToKey={ROW_TO_KEY}
          sorter={sorter || DEFAULT_SORTER}
          sortDirection={dir}
          renderHead={true}
          headProps={{ sticky: true }}
          onHeadClick={(c, d) => setSort([SORTERS[c.key] || DEFAULT_SORTER, d])}
          chunkSize={30}
          rootMargin="300px"
          rowHeight={ROW_HEIGHTS.dense}
          otherProps={undefined}
        />
      )}
      <CreateAdminUserDialog open={dialogOpen} onClose={closeDialog} />
    </Section>
  );
};
